import {
  decodeURLString,
  FatalErrors,
  fetchWrapper,
  getIslandType,
  getServerRoomName,
  iFrameLauncherEvent,
  type iFrameLauncherMessage,
  LauncherEventType,
  mocksResponse,
  type MonsterversePerfStats,
  type OnAppTerminatingData,
} from "@classdojo/monsterverse-utils";
import { IslandType } from "@monsterverse/island-types";
import { decode, encode } from "@msgpack/msgpack";
import { Client, type Room } from "colyseus.js";

import { getHomeServerPort } from "./AWSHostURLs";
import { getConsecutiveError, incrementConsecutiveError, resetConsecutiveError } from "./ConsecutiveErrorCounter";

const pingTimerInSeconds = 30;
const noActivityTimeoutInSeconds = 600;
const backgroundTimeoutInSeconds = 30;
const reconnectionDelayInSeconds = 1;
const reconnectionDelayVarianceInSeconds = 3;
const totalReconnectionAttempts = 5;

export type IframeCallbackMessage = {
  iFrameLauncherEvent: typeof iFrameLauncherEvent;
  type: LauncherEventType;
  data: string | ArrayBuffer | undefined;
};

export type IframeCallbacks = {
  postMessage: (message: IframeCallbackMessage) => void;
  removeIFrame: () => void;
  triggerFatalError: (error: FatalErrors, hostname?: string) => void;
  onChangeIsland: (islandType: "class" | "home" | "community" | "personal", islandId: string) => void;
};

export type LoggerCallbacks = {
  logPerfStats: (stats: MonsterversePerfStats) => void;
  logEvent: (event: string) => void;
  logInfo: (message: string, context?: Record<string, unknown>) => void;
};

export class Networking {
  private _client?: Client;
  private _room?: Room<unknown>;
  private _URL: URL;

  private _receivedMessage = false;
  private _reconnectAttemptTimeout?: number;
  private _reconnectionAttemptsLeft = totalReconnectionAttempts;
  private _connected = false;

  private _timeoutTimer?: number;
  private _backgroundTimeoutTimer?: number;
  private _skipNextTimeoutReset = false;

  private _perfTier = -1;
  private _pingInterval?: number;
  private _pingSentTime = Date.now();
  private _receivedMessageCount = 0;
  private _sentMessageCount = 0;

  public loadingComplete = false;

  private _originalGroupId: string;
  private _originalTicket: string;

  constructor(
    private _ENV: string,
    private _iframeCallbacks: IframeCallbacks,
    private _Logger: LoggerCallbacks,
    wsUrl: URL,
    private _groupId: string,
    private _countryCode: string,
    private _serverRegion: string,
    private _desiredServerRegion: string
  ) {
    this._originalGroupId = _groupId;
    this._originalTicket = wsUrl.searchParams.get("ticket") ?? "";

    this._URL = wsUrl;

    this._connect(false);
  }

  public onAppLoadingComplete() {
    this.loadingComplete = true;
    this._resetInactivityTimer();
  }

  public onAppTerminating(data: OnAppTerminatingData) {
    const handler = data;
    if (handler) {
      const { exitingMonsterverse, customizer } = handler;
      if (!exitingMonsterverse && customizer) {
        this.loadingComplete = false;
        this._clearTimeoutTimer();
        this._clearBackgroundInactivityTimer();
      }
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  private async _validateTicketAndConnect() {
    if (this._ENV !== "local") {
      //Build the check ticket url
      const checkTicketUrl = new URL(this._URL.toString());
      checkTicketUrl.protocol = "https:";
      checkTicketUrl.pathname = "/checkTicket";

      let invalidTicket = false;
      try {
        //Check our ticket
        const response = await fetchWrapper("ticketCheck", checkTicketUrl.toString(), { method: "GET" });

        //Failed response
        if (!response.ok) {
          const responseText = await response.text();
          invalidTicket = responseText === "Invalid server ticket";
          throw new Error(`ticketCheck: status failure ${responseText}`);
        }

        //Update ticket with refreshed value
        try {
          const result = await response.json();
          this._URL.searchParams.set("ticket", result.ticket);
        } catch {
          throw new Error(`ticketCheck: failed to decode json response`);
        }
      } catch (e) {
        this._reconnectionAttemptsLeft--;

        // Reset the consecutive websocket closure and bundle load error counts if we hit an error with the ticket validation.
        // This is to prevent the error modals claiming explicit network shaping issues when it may still be flaky internet.
        resetConsecutiveError(FatalErrors.WebsocketClosure);
        resetConsecutiveError(FatalErrors.BundleLoadError);

        if (this._reconnectionAttemptsLeft <= 0 || invalidTicket) {
          if (e instanceof Error && "type" in e && e.type === "FetchError") {
            const fetchError = e as Record<string, unknown>;
            this._Logger.logInfo("ticketCheck: Failed to fetch the route", {
              category: fetchError.category,
              url: fetchError.url,
              origError: fetchError.origError,
            });
          } else if (e instanceof Error) {
            this._Logger.logInfo(e.message);
          } else {
            this._Logger.logInfo("ticketCheck: Unknown failure");
          }

          this._disconnect(invalidTicket ? FatalErrors.ServerTicketExpiredTimeout : FatalErrors.ServerReconnectTimeout);
        } else {
          this._startReconnectAttemptTimer();
        }

        //Prevent connection from happening below
        return;
      }
    }

    this._connect(true);
  }

  private _connect(reconnecting: boolean) {
    this._client = new Client(this._URL.toString());

    const connectOptions: Record<string, string> = {};
    for (const [key, value] of this._URL.searchParams.entries()) {
      connectOptions[key] = value;
    }

    //Overwrite the endpoint functions to support params (ticket and logger session id)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const origHttpEndpoint = (this._client as any).getHttpEndpoint.bind(this._client);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this._client as any).getHttpEndpoint = (segments = "") => {
      return `${origHttpEndpoint(segments)}?${this._URL.searchParams.toString()}`;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const origWsEndpoint = (this._client as any).buildEndpoint.bind(this._client);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this._client as any).buildEndpoint = (room: any, options: any = {}) => {
      return origWsEndpoint(room, { ...options, ...connectOptions });
    };

    //Remove headers to prevent CORS requests that we don't need
    const origHttpPost = this._client.http.post.bind(this._client.http);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this._client.http.post = (path: string, options: any = {}) => {
      delete options["headers"];
      return origHttpPost(path, options);
    };

    this._client
      .joinOrCreate(getServerRoomName(this._groupId), connectOptions)
      .then((room) => this._onJoin(room, reconnecting))
      .catch((reason) => {
        this._postMessage(LauncherEventType.NetworkLosing, JSON.stringify({ isPlaygroundServer: false }));

        //If you lose connection, ensure you connect back to your original server
        //Trying to connect back to playground will leave you in a weird state as we don't support late join
        this._groupId = this._originalGroupId;
        this._URL.searchParams.set("ticket", this._originalTicket);

        if (reason instanceof Error && reason.message.includes("VIPNoPlots")) {
          this._disconnect(FatalErrors.VIPNoPlots);
          return;
        }

        this._reconnectionAttemptsLeft--;
        if (this._reconnectionAttemptsLeft <= 0) {
          if (reason instanceof Error) {
            if ("code" in reason) {
              this._Logger.logInfo(`Colyseus error: ${reason.name} code ${reason.code} - ${reason.message}`);
            } else {
              this._Logger.logInfo(`Colyseus error: ${reason.name} - ${reason.message}`);
            }
          } else {
            this._Logger.logInfo(`Colyseus error: unknown type - ${JSON.stringify(reason)}`);
          }

          this._Logger.logPerfStats({
            type: "NetworkingConnection",
            site: "launcher",
            status: "disconnected",
          });

          // Increment the number of consecutive websocket closure errors.
          incrementConsecutiveError(FatalErrors.WebsocketClosure);

          // Then handle disconnection.
          this._disconnect(FatalErrors.WebsocketClosure);
        } else {
          this._startReconnectAttemptTimer();
        }
      });

    this._receivedMessage = false;
  }

  private _onJoin(room: Room<unknown>, reconnecting: boolean) {
    this._room = room;

    this._room.onMessage("*", (_t, message) => {
      if (!this._receivedMessage) {
        this._Logger.logEvent("monsterverse.launcher.websocketConnected");
        this._receivedMessage = true;
      }

      this._postMessage(LauncherEventType.NetworkMessage, message);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const decoded = decode(message) as any;
      const isLauncher = "launcher" in decoded;
      if (isLauncher) {
        this._onMessage(decoded);
      }
    });

    this._room.onLeave((_code) => {
      //If you lose connection, ensure you connect back to your original server
      //Trying to connect back to playground will leave you in a weird state as we don't support late join
      this._groupId = this._originalGroupId;
      this._URL.searchParams.set("ticket", this._originalTicket);

      if (this._pingInterval) {
        clearInterval(this._pingInterval);
        this._pingInterval = undefined;
      }

      this._postMessage(LauncherEventType.NetworkLosing, JSON.stringify({ isPlaygroundServer: false }));
      this._connected = false;

      this._Logger.logPerfStats({
        type: "NetworkingConnection",
        site: "launcher",
        status: "lost",
      });
      this._startReconnectAttemptTimer();
    });

    console.log("websocket is open, sending join");
    this._Logger.logEvent("monsterverse.launcher.websocketOpen");
    this._Logger.logPerfStats({ type: "NetworkingConnection", site: "launcher", status: "connected" });

    this._reconnectionAttemptsLeft = totalReconnectionAttempts;

    if (this._reconnectAttemptTimeout) {
      clearTimeout(this._reconnectAttemptTimeout);
      this._reconnectAttemptTimeout = undefined;
    }

    if (!reconnecting) {
      this._clearTimeoutTimer();
      this._clearBackgroundInactivityTimer();
    } else {
      this._skipNextTimeoutReset = true;
    }

    const isPlaygroundServer = getIslandType(this._groupId) === IslandType.Playground;
    this._postMessage(LauncherEventType.NetworkConnected, JSON.stringify({ isPlaygroundServer }));
    this._connected = true;

    this._sendPing();
    this._pingInterval = setInterval(this._sendPing.bind(this), pingTimerInSeconds * 1000) as unknown as number;

    // We've established a websocket connection; reset the websocket closure counter.
    resetConsecutiveError(FatalErrors.WebsocketClosure);
  }

  private _disconnect(fireDisconnectEvent: FatalErrors) {
    //Close websocket and remove event listeners
    if (this._room) {
      this._room.removeAllListeners();
      this._room.leave(true);
    }

    //Clear out the websocket
    this._room = undefined;

    if (this._reconnectAttemptTimeout) {
      clearTimeout(this._reconnectAttemptTimeout);
      this._reconnectAttemptTimeout = undefined;
    }

    this._clearTimeoutTimer();
    this._clearBackgroundInactivityTimer();

    this._postMessage(LauncherEventType.NetworkDisconnected);
    this._connected = false;

    //Clear received message indicator
    this._receivedMessage = false;

    // Handle websocket closure escalation if needed
    if (fireDisconnectEvent === FatalErrors.WebsocketClosure) {
      const error =
        getConsecutiveError(FatalErrors.WebsocketClosure) > 1
          ? FatalErrors.WebsocketClosureEscalated
          : FatalErrors.WebsocketClosure;
      this._iframeCallbacks.triggerFatalError(error, this._URL.hostname);
      return;
    }

    this._iframeCallbacks.triggerFatalError(fireDisconnectEvent);
  }

  private _sendPing() {
    if (this._room === undefined) {
      return;
    }

    //Send ping to keep connection alive
    this._room.sendBytes("all", encode({ type: "Ping", launcher: true }) as unknown as ArrayBufferLike);
    this._sentMessageCount++;
    this._pingSentTime = Date.now();
  }

  private _startReconnectAttemptTimer() {
    if (this._reconnectAttemptTimeout !== undefined) {
      return;
    }

    this._reconnectAttemptTimeout = setTimeout(
      () => {
        this._reconnectAttemptTimeout = undefined;
        this._validateTicketAndConnect();
      },
      (reconnectionDelayInSeconds + Math.random() * reconnectionDelayVarianceInSeconds) * 1000
    ) as unknown as number;
  }

  private _onMessage(message: { type: string; data?: string }) {
    switch (message.type) {
      case "Ping":
        this._receivedMessageCount++;
        this._Logger.logPerfStats({
          type: "Networking",
          site: "launcher",
          sentMessages: this._sentMessageCount,
          receivedMessages: this._receivedMessageCount,
          ping: Date.now() - this._pingSentTime,
          performanceTier: this._perfTier,
          countryCode: this._countryCode,
          serverRegion: this._serverRegion,
          desiredServerRegion: this._desiredServerRegion,
        });
        this._sentMessageCount = 0;
        this._receivedMessageCount = 0;
        break;
      case "ServerShuttingDown":
        console.log("Got server shutting down");
        this._disconnect(FatalErrors.ServerShuttingDown);
        break;
      case "RemovePlayer":
        console.log("Got player removal");
        this._disconnect(FatalErrors.RemovePlayer);
        break;
    }

    this._receivedMessageCount++;
  }

  private _postMessage(type: LauncherEventType, data: string | ArrayBuffer | undefined = undefined) {
    this._iframeCallbacks.postMessage({ iFrameLauncherEvent, type, data });
  }

  private _everConnectedToPlaygroundServer = false;

  public onIFrameMessage(data: iFrameLauncherMessage) {
    if (data?.iFrameLauncherEvent !== iFrameLauncherEvent) {
      return;
    }

    switch (data.type) {
      case LauncherEventType.NetworkMessage:
        if (data.data) {
          this._onNetworkMessage(data.data as Uint8Array);
        }
        break;
      case LauncherEventType.ChangeIsland:
        if (data.data) {
          const { islandType, islandId } = JSON.parse(data.data as string);
          console.log("Got request to change island with", islandType, islandId);
          this._iframeCallbacks.onChangeIsland(islandType, islandId);
        }
        break;
      case LauncherEventType.Reload:
        // TODO: should we be hard terminating here instead of just reloading?
        this._iframeCallbacks.removeIFrame();
        break;
      case LauncherEventType.IframeLoaded:
        if (this._room && this._connected) {
          const isPlaygroundServer = getIslandType(this._groupId) === IslandType.Playground;
          this._postMessage(
            LauncherEventType.NetworkConnected,
            JSON.stringify({ isPlaygroundServer, sessionId: this._room.sessionId })
          );
        }
        break;
      case LauncherEventType.SetPerfTier:
        this._perfTier = data.data as number;
        break;
      case LauncherEventType.LostFocus:
        this._resetBackgroundInactivityTimer();
        break;
      case LauncherEventType.GainedFocus:
        this._clearBackgroundInactivityTimer();
        break;
      case LauncherEventType.SwapServers:
        {
          console.log("!!!Switching servers");
          //Parse the ticket data
          if (data.data) {
            const { groupId, ticket } = JSON.parse(data.data as string);
            this._groupId = groupId;
            this._URL.searchParams.set("ticket", ticket);

            if (mocksResponse()) {
              // When using local dual servers, one of the servers clients connect on 8081.
              // We need to update the URL to connect to the correct server.
              try {
                const ticketData = JSON.parse(decodeURLString(ticket));
                if (ticketData.host && ticketData.port) {
                  this._URL.hostname = ticketData.host;
                  this._URL.port = ticketData.port.toString();
                  console.log(`Updated URL for playground: ${this._URL.toString()}`);
                }
              } catch (e) {
                console.warn("Failed to parse playground ticket for URL update:", e);
              }
            }
          } else {
            this._groupId = this._originalGroupId;
            this._URL.searchParams.set("ticket", this._originalTicket);

            // When using dual servers, return to the server you came from
            if (mocksResponse()) {
              const wsPort = getHomeServerPort();
              this._URL.port = wsPort;
            }
          }

          //Disconnect from the current server
          this._room?.onLeave.clear();
          if (this._pingInterval) {
            clearInterval(this._pingInterval);
            this._pingInterval = undefined;
          }

          this._postMessage(LauncherEventType.NetworkLosing, JSON.stringify({ isPlaygroundServer: true }));
          this._connected = false;

          this._room?.leave(true);

          //Connect to the new server
          this._validateTicketAndConnect();
        }
        break;
    }
  }

  private _onNetworkMessage(data: Uint8Array) {
    if (this._room === undefined) {
      return;
    }

    this._room.sendBytes("all", data as unknown as ArrayBufferLike);
    this._sentMessageCount++;
    this._resetInactivityTimer();
  }

  private _clearBackgroundInactivityTimer() {
    if (this._backgroundTimeoutTimer) {
      clearTimeout(this._backgroundTimeoutTimer);
      this._backgroundTimeoutTimer = undefined;
    }
  }

  private _resetBackgroundInactivityTimer() {
    if (this._ENV !== "local" && this.loadingComplete && !this._skipNextTimeoutReset) {
      this._clearBackgroundInactivityTimer();

      this._backgroundTimeoutTimer = setTimeout(() => {
        console.log("Got background inactivity timeout");
        this._disconnect(FatalErrors.ServerIdleTimeout);
      }, backgroundTimeoutInSeconds * 1000) as unknown as number;
    }
    this._skipNextTimeoutReset = false;
  }

  private _clearTimeoutTimer() {
    if (this._timeoutTimer) {
      clearTimeout(this._timeoutTimer);
      this._timeoutTimer = undefined;
    }
  }

  private _resetInactivityTimer() {
    if (this._ENV !== "local" && this._room && this.loadingComplete && !this._skipNextTimeoutReset) {
      this._clearTimeoutTimer();

      this._timeoutTimer = setTimeout(() => {
        console.log("Got inactivity timeout");
        this._disconnect(FatalErrors.ServerIdleTimeout);
      }, noActivityTimeoutInSeconds * 1000) as unknown as number;
    }
    this._skipNextTimeoutReset = false;
  }
}
